/**
 * RightRail — the desktop-only right column of the logged-in app shell.
 *
 * Fills the empty space to the right of the main content on wide screens
 * (≥1280px) with genuinely useful, REAL content — no fabricated numbers:
 *  1. TB Helpline card   — the verified Government of India TB helpline
 *                          (1800-11-6666, tel: link) + a link to the official
 *                          tbcindia.mohfw.gov.in portal.
 *  2. News & Updates     — the latest curated news items pulled from the real
 *                          backend (`GET /news/top`), each linking to its
 *                          official source.
 *  3. Quick actions      — Find a TB Centre / Symptom Checker / Become a
 *                          Supporter (real routes already in the app).
 *
 * Hidden below `xl` (mobile/tablet get the left-nav drawer + bottom nav only).
 * Sticky, independently scrollable so it stays in view as the page scrolls.
 *
 * Labels resolve from the `rail.*` i18n namespace (EN/HI/MR). Quick-action
 * labels reuse the existing `nav.*` keys (already three-way translated).
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getTopNews, type NewsItem } from '@/services/newsService'
import { Phone, ExternalLink, MapPin, Thermometer, HandHeart, Newspaper, ArrowUpRight, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

const HELPLINE_TEL = '1800116666'
const HELPLINE_DISPLAY = '1800-11-6666'
const OFFICIAL_PORTAL_URL = 'https://tbcindia.mohfw.gov.in/'

const QUICK_ACTIONS = [
  { key: 'findTbCentre', path: '/find-tb-centre', icon: MapPin },
  { key: 'symptomChecker', path: '/symptom-checker', icon: Thermometer },
  { key: 'becomeSupporter', path: '/become-a-supporter', icon: HandHeart },
] as const

function fmtDate(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

export function RightRail() {
  const { t } = useTranslation()
  const [items, setItems] = useState<NewsItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    getTopNews(4)
      .then((news) => { if (mounted) { setItems(news); setLoading(false) } })
      .catch(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [])

  return (
    <aside
      aria-label={t('rail.newsTitle', 'News & Updates')}
      className="hidden xl:block w-80 shrink-0 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto border-l border-gray-100 bg-white"
    >
      <div className="px-4 py-4 space-y-4">

        {/* 1. TB Helpline */}
        <section className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-cyan-50 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Phone className="w-4 h-4 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-900">{t('rail.helplineTitle')}</h2>
          </div>
          <p className="text-xs text-gray-500 mb-3">{t('rail.helplineDesc')}</p>
          <a
            href={`tel:${HELPLINE_TEL}`}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition-colors"
            aria-label={t('header.helplineAria', 'TB helpline 1800 11 6666')}
          >
            <Phone className="w-4 h-4" />
            <span>{HELPLINE_DISPLAY}</span>
          </a>
          <a
            href={OFFICIAL_PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg border border-blue-200 text-blue-700 text-xs font-medium hover:bg-blue-50 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            <span>{t('rail.officialPortal')}</span>
          </a>
        </section>

        {/* 2. News & Updates */}
        <section className="rounded-xl border border-gray-100 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Newspaper className="w-4 h-4 text-gray-500" />
              <h2 className="text-sm font-semibold text-gray-900">{t('rail.newsTitle')}</h2>
            </div>
            <Link to="/news" className="text-xs font-medium text-blue-600 hover:underline flex items-center gap-0.5">
              {t('rail.newsAll')}<ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-6 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-xs text-gray-500 py-2 leading-relaxed">{t('rail.newsEmpty')}</p>
          ) : (
            <ul className="space-y-3">
              {items.map((n) => (
                <li key={n.id} className="border-b border-gray-50 last:border-0 pb-3 last:pb-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    {n.isPinned && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold uppercase tracking-wide">
                        {t('rail.pinned')}
                      </span>
                    )}
                    {n.publishedAt && (
                      <span className="text-[11px] text-gray-400">{fmtDate(n.publishedAt)}</span>
                    )}
                  </div>
                  <h3 className="text-xs font-semibold text-gray-800 leading-snug mb-0.5 line-clamp-2">
                    {n.title}
                  </h3>
                  {n.summary && (
                    <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-2 mb-1">{n.summary}</p>
                  )}
                  {n.url && (
                    <a
                      href={n.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline"
                    >
                      {t('rail.readOfficial')}<ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 3. Quick actions */}
        <section className="rounded-xl border border-gray-100 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <HandHeart className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-900">{t('rail.quickActions')}</h2>
          </div>
          <div className="space-y-2">
            {QUICK_ACTIONS.map(({ key, path, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={cn(
                  'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg border border-gray-100 text-sm font-medium text-gray-700',
                  'hover:bg-gray-50 hover:border-gray-200 transition-colors',
                )}
              >
                <Icon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <span className="truncate">{t(`nav.${key}`)}</span>
                <ArrowUpRight className="w-3.5 h-3.5 text-gray-400 ml-auto flex-shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </aside>
  )
}

export default RightRail